import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { useMutation } from "@tanstack/react-query";

import { useScheduleFetch } from "../hooks/useScheduleFetch";
import { ScheduleForm } from "../../Sales/Contracts/CreateJobAndSchedule/ScheduleForm/ScheduleForm";
import { requests } from "../../../services/http-common";
import { useToast } from "../../../Providers/ToastProvider";

import classes from "./ScheduleEdit.module.scss";
import { PrivateAccess } from "../../../keycloak";
import { UserRoles } from "../../../constants/userRoles";
import { ConfirmationModal } from "../../../components/ConfirmationModal/ConfirmationModal";

export function ScheduleEdit() {
  const { show } = useToast();

  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  const { scheduleId } = useParams();
  const { form, scheduleData } = useScheduleFetch(Number(scheduleId));

  const navigate = useNavigate();

  const updateSchedule = useMutation({
    mutationFn: (newSchedule: any) => {
      return requests.put(`/job/jobSchedule/${scheduleId}`, newSchedule);
    },
    onSuccess: () => {
      show({ detail: "Schedule was created successfully !" });
      navigate("/scheduling-execution/schedule");
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteSchedule = useMutation({
    mutationFn: (scheduleId: any) => {
      return requests.delete(`/job/jobSchedule/${scheduleId}`);
    },
    onSuccess: (_, id) => {
      show({ detail: "Schedule was deleted successfully !" });
      navigate(-1);
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleUpdateClick = () => {
    form.handleSubmit(async (formData) => {
      const attachmentsPromises: any = [];
      if (formData.attachments?.length) {
        formData.attachments.forEach((doc) => {
          if (doc.size) {
            const formData = new FormData();
            formData.append("attachment", doc);
            formData.append("leadId", `${scheduleData?.jobDetail.leadId}`);

            attachmentsPromises.push(
              requests.post("/job/attachment", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                transformRequest: (formData: any) => formData,
              })
            );
          }
        });
      }

      const attachment2Path = await Promise.all(attachmentsPromises);
      await requests.put(`/job/jobDetail/${scheduleData?.jobDetail.id}`, {
        attachment2Path,
      });

      updateSchedule.mutate(formData);
    })();
  };

  return (
    <div>
      <ScheduleForm
        form={form}
        jobId={scheduleData?.jobDetail.id}
        isScheduleEdit
      />
      <ConfirmationModal
        visible={showDeletePopup}
        onHide={() => setShowDeletePopup(false)}
        accept={() => deleteSchedule.mutate(scheduleId)}
        reject={() => setShowDeletePopup(false)}
      />
      <div className={classes.actions}>
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.JOB_DELETE],
          }}
        >
          <Button
            icon="pi pi-trash"
            className="p-button-danger p-button-sm"
            onClick={() => setShowDeletePopup(true)}
          />
        </PrivateAccess>
        <Button
          label="Cancel"
          type="reset"
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate(-1)}
        />
        <Button
          label="Update"
          disabled={updateSchedule.isLoading}
          onClick={handleUpdateClick}
        />
      </div>
    </div>
  );
}
